import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useCustomPreview from '../../hooks/useCustomPreview';
import { DEVICE_ACCESS_STATUS } from '../../components/VideoStream/constants';
import AuthContext from '../../context/AuthContext';
import { useEffectOnce } from '../../hooks/useEffectOnce';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DemoContext from 'context/DemoContext';
import { calculateDifferenceTodayPublic } from 'utils/dateFormatter';
import { DEMO_STATUS_OPTIONS, GENERIC_ERROR_MESSAGE } from 'utils/constants';
import { sendGuestOtp, verifyGuestOtp } from 'Services/demoService';
import CustomModal from 'components/Modal/CustomModal';
import DemoMessageOut from 'components/Demos-Message/MessageOutOfCall';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { Button, message } from 'antd';
import VideoContext from 'context/VideoContext';
import PurebrandLoader from 'components/Loader/PurebrandLoader';
import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import PermissionsChecker from './Permissions-checker';
import {collectDeviceInfo} from 'components/VideoStream/DeviceInfo';
// NEW: Extra guest form & API
import ExtraGuestForm from "./ExtraGuestForm";
import { registerExtraGuest } from "Services/demoService";
import { getHostProductCategories } from "Services/demoService";
import { validateGuest } from "Services/demoService"; // ADD


import { parsePhoneNumberWithError, formatIncompletePhoneNumber, AsYouType } from 'libphonenumber-js';
import logger from 'utils/logger';
import GuestPopupTerms from 'components/Agreements/GuestPopupTerms';
import "./TermsModal.css"


import WaitingRoomPreview from "./WaitingRoomPreview";

async function detectIfFrontCamera() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((d) => d.kind === 'videoinput');
    if (!videoInputs || videoInputs.length === 0) {
      return true;
    }

    const labeled = videoInputs.find((d) => d.label && d.label.length > 0);
    const target = labeled || videoInputs[0];
    const label = (target.label || '').toLowerCase();
    if (label.includes('front') || label.includes('user')) return true;
    if (label.includes('back') || label.includes('environment') || label.includes('rear')) return false;

    return true;
  } catch (e) {
    return true;
  }
}

/**
 * Formats a phone number according to the country's format
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number or original if formatting fails
 */
function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return phoneNumber;

  try {
    // Try to parse the phone number (it should include country code)
    // Using parsePhoneNumberWithError to avoid deprecation warning
    const phoneNumberObj = parsePhoneNumberWithError(phoneNumber);
    if (phoneNumberObj && phoneNumberObj.isValid()) {
      // Format according to national format which includes country-specific formatting
      // This will show formats like (234) 567-8900 for US, +44 20 1234 5678 for UK, etc.
      return phoneNumberObj.formatNational();
    }
    // If parsing fails, try to format as incomplete number
    return formatIncompletePhoneNumber(phoneNumber);
  } catch (error) {
    // If parsing fails, try using AsYouType formatter for partial numbers
    try {
      const formatter = new AsYouType();
      const formatted = formatter.input(phoneNumber);
      return formatted || phoneNumber;
    } catch (formatError) {
      // If all formatting fails, return the original number
      return phoneNumber;
    }
  }
}

/**
 * This component is used to render the waiting room for a host or a guest for the demo preview.
 *
 * @param {void}
 * @return {ReactComponent} It returns a React component for rendering the waiting room for a host or a guest for the demo preview.
 */

async function fetchUserIp() {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return data.ip;
  } catch (err) {
    console.error("IP fetch failed", err);
    return null;
  }
}

/**
 * Detects whether microphone and camera permissions are granted by attempting
 * to access each media device separately via the browser MediaDevices API.
 *
 * This is used only to capture the user's current permission state at the time
 * they click "Join Call".
 *
 * @async
 * @function getMediaPermissions
 * @returns {Promise<{audio: boolean, video: boolean}>} An object containing:
 * - `audio`: `true` if microphone permission is granted, otherwise `false`
 * - `video`: `true` if camera permission is granted, otherwise `false`
 */
const getMediaPermissions = async () => {
  let audio = false;
  let video = false;

  try {
    const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    audio = true;
    audioStream.getTracks().forEach((track) => track.stop());
  } catch (err) {
    audio = false;
  }

  try {
    const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
    video = true;
    videoStream.getTracks().forEach((track) => track.stop());
  } catch (err) {
    video = false;
  }

  return { audio, video };
};


export default function WaitingRoom() {
  const navigate = useNavigate();
  const location = useLocation();
  const { demoId } = useParams();
  // NEW: extra guest flags
  const [isNewExtraGuest, setIsNewExtraGuest] = useState(false);
  const [guestValidationCompleted, setGuestValidationCompleted] = useState(false);
  


  const runGuestValidation = async () => {
    try {

      const ip = await fetchUserIp();
      const userId = localStorage.getItem("extra_guest_id") || null;

      const res = await validateGuest({
        demo_id: demoId,
        ip_address: ip,
      user_id: userId
      });

      const data = res?.data;

      if (data.is_registered && !data.is_new_guest && data.guest) {
    setEventDetail(prev => ({
          ...prev,
          members: {
            ...prev?.members,
            guest_detail: {
              id: data.guest.id,
              full_name: data.guest.full_name,
              phone_number: data.guest.phone_number,
              email: data.guest.email,
              is_otp_verified: data.guest.is_otp_verified,
            },
          },
        }));

        localStorage.setItem("extra_guest_id", data.guest.id);
        localStorage.setItem("username", data.guest.full_name);
        localStorage.setItem("phone_number", data.guest.phone_number);
        localStorage.setItem("email", data.guest.email);
        localStorage.setItem("is_otp_verified", data.guest.is_otp_verified);

        setIsNewExtraGuest(false);
      }

      if (!data.success) {
        setIsNewExtraGuest(true);
        return;
      }

      if (data.is_primary_guest === true) {
        setIsNewExtraGuest(false);
        return;
      }

      if (data.is_primary_guest === false) {
        setIsNewExtraGuest(false);
        return;
      }

      if (data.is_registered === true && data.is_new_guest === true) {
        setIsNewExtraGuest(true);
        return;
      }

      if (data.is_new_guest === true) {
        setIsNewExtraGuest(true);
        return;
      }

    } catch (err) {
      console.log("Guest validation failed", err);
      setIsNewExtraGuest(true);
    }
  };

  const [extraGuestRegistered, setExtraGuestRegistered] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { user, setUser, loggedIn } = useContext(AuthContext);
  const [storeCategories, setStoreCategories] = useState([]);
  const {
    setDataForMeeting,
    setHostPresence,
    publicDemoStatus,
    setGuestPresence,
    authorizeHost,
  } = useContext(DemoContext);
  const { getVideoCredentials, setDemoId, setVideoCredentials } =
    useContext(VideoContext);
  const waitingRoomVideoContainer = useRef();
  const [userName, setUserName] = useState('');
  const [isUserNameInvalid, setIsUserNameInvalid] = useState(false);
  const [localAudio, setLocalAudio] = useState(true);
  const [localVideo, setLocalVideo] = useState(true);
  const [sendMessageBox, setSendMessageBox] = useState(false);
  const shouldShowWaitingRoom =
  loggedIn || (!isNewExtraGuest || extraGuestRegistered);
  const [cameraMicrophonePermission, setCameraMicrophonePermission] =
    useState(false);
  const [eventDetail, setEventDetail] = useState({});
  useEffect(() => {
    const storeIdentifier = eventDetail?.store?.identifier;
    const hostId = eventDetail?.members?.host_detail?.id;

    if (storeIdentifier && hostId) {
      getHostProductCategories({
        store_identifier: storeIdentifier,
        host_id: hostId,
      })
        .then(res => {
          setStoreCategories(res?.data?.data || []);
        })
        .catch(err => console.log("ERROR fetching host categories:", err));
    }
  }, [eventDetail?.members?.host_detail?.id]);

  const [authorizationError, setAuthorizationError] = useState(false);
  const [authorizationErrorMessage, setAuthorizationErrorMessage] =
    useState('Invalid Request');
  const [authorizationLoading, setAuthorizationLoading] = useState(true);
  const [joinLoading, setJoinLoading] = useState(false);
  const [otpState, setOtpState] = useState({
    value: '',
    verified: false,
    sending: false,
    loading: false,
    resendCountdown: 0,
    sentOnce: false,
    error: ''
  });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const {
    createPreview,
    destroyPreview,
    previewPublisher,
    accessAllowed,
    deviceInfo,
  } = useCustomPreview();

  const appID = process.env.REACT_APP_ZEGO_CLOUD_APP_ID;
  const serverSecret = process.env.REACT_APP_ZEGO_CLOUD_SERVER_SECRET;

  // Test demo detection - bypass authentication for test IDs
  const isTestDemo = demoId?.startsWith('test-');

  // Mirror state: only mirror when using front camera
  const [isFrontCamera, setIsFrontCamera] = useState(true);

  const handleSendMessageClose = () => {
    setSendMessageBox(false);
  };

  // 🔥 NEW: Extract username from query parameters for testing
  const [hasUrlUserName, setHasUrlUserName] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paramUserName = queryParams.get('userName') || queryParams.get('name');
    if (paramUserName) {
      setUserName(decodeURIComponent(paramUserName));
      setHasUrlUserName(true);
    }
  }, []);

  // Function to clean up video and audio streams
  const cleanupStreams = () => {
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      if (video.srcObject) {
        const stream = video.srcObject;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach(track => {
            track.stop();
          });
        }
        video.srcObject = null;
      }
    });

    const audios = document.querySelectorAll('audio');
    audios.forEach(audio => {
      if (audio.srcObject) {
        const stream = audio.srcObject;
        if (stream && stream.getTracks) {
          stream.getTracks().forEach(track => {
            track.stop();
          });
        }
        audio.srcObject = null;
      }
    });
  };


  const redirectHttps = React.useCallback(() => {
    const url = window.location.href;
    if (
      url.toString().indexOf('http://') === 0 &&
      url.toString().indexOf('http://localhost') !== 0
    ) {
      window.location.href = window.location.href
        .toString()
        .replace('http://', 'https://');
    } else {
      return;
    }
  }, []);

  const getName = () => {
    // Test demos use mock data instead of API calls
    if (isTestDemo) {
      const mockEventDetail = {
        id: demoId,
        status: 'SCHEDULED',
        members: {
          host_detail: {
            id: 'test-host-id',
            first_name: 'Test',
            last_name: 'Host',
          },
          guest_detail: {
            id: 'test-guest-id',
            full_name: 'Test Guest',
          },
        },
        meeting_details: {
          session_id: `session-${demoId}`,
          guest_token: 'test-token',
        },
      };
      setEventDetail(mockEventDetail);
      // 🔥 Only set default username if URL parameter wasn't provided
      // Check URL params directly to avoid race condition with async state updates
      const queryParams = new URLSearchParams(window.location.search);
      const paramUserName = queryParams.get('userName') || queryParams.get('name');
      if (!paramUserName) {
        setUserName('Test User');
      }
      return;
    }

    let storedGuestIdForStatus = localStorage.getItem("extra_guest_id");
    if (!storedGuestIdForStatus) {
      try {
        const storedDetails = JSON.parse(localStorage.getItem("DETAILS") || "null");
        storedGuestIdForStatus =
          storedDetails?.id ||
          storedDetails?.sessionDetail?.members?.guest_detail?.id ||
          null;
      } catch (err) {
        storedGuestIdForStatus = null;
      }
    }
    const res = publicDemoStatus(
      demoId,
      storedGuestIdForStatus,
      storedGuestIdForStatus ? "uid" : "none"
    );
    res
      .then((response) => {
        const data = response?.data?.data;
        const storedGuestId = localStorage.getItem("extra_guest_id");
        const storedGuestName = localStorage.getItem("username");
        const storedPhone = localStorage.getItem("phone_number");
        const storedEmail = localStorage.getItem("email");
        const storedIsOtpVerified = localStorage.getItem("is_otp_verified");

        // Extra guests are not returned in members.guest_detail (that holds the
        // primary guest, which is null for extra-guest demos). When a returning
        // extra guest is present in localStorage, rehydrate guest_detail before
        // committing eventDetail so the name field, OTP guest_id, and verified
        // state are restored instead of being wiped to null.
        if (storedGuestId && storedGuestName) {
          data.members = data.members || {};
          data.members.guest_detail = {
            id: storedGuestId,
            full_name: storedGuestName,
            phone_number: storedPhone,
            email: storedEmail,
            is_otp_verified: storedIsOtpVerified === "true",
          };
        }

        setEventDetail({ ...data });

        // initialize OTP state if backend already verified
        const guestIsVerified =
          storedIsOtpVerified === "true" ||
          !!data?.members?.guest_detail?.is_otp_verified;
        if (guestIsVerified) {
          setOtpState(prev => ({ ...prev, verified: true, sentOnce: true }));
        } else {
          setOtpState(prev => ({ ...prev, verified: false }));
        }
        if (user && loggedIn) {
          if (response?.data?.data?.members?.host_detail?.first_name) {
            setUserName(
              `${response.data.data.members.host_detail.first_name} ${response.data.data.members.host_detail.last_name}`
            );
          } else {
            setUserName(`${user?.firstName} ${user?.lastName}`);
          }
        } else {
          setUserName(data?.members?.guest_detail?.full_name || '');
        }
      })
      .catch((err) => {
        setUserName('');
      });
  };

  // OTP helpers
  const isOtpRequired = () => {
    if (!eventDetail) return false;


    return (
      Object.keys(eventDetail?.meeting_details || {}).length !== 0 &&
      eventDetail?.status !== DEMO_STATUS_OPTIONS.ACCEPTED &&
      eventDetail?.status !== 'ENDED' &&
      eventDetail?.status !== 'CANCELED'
    );
  };

  const handleExtraGuestSubmit = async (formData) => {
    try {
      const ip = await fetchUserIp();

      const res = await registerExtraGuest({
        demo_id: demoId,
        guest_full_name: formData.guest.full_name,
        phone_number: formData.guest.phone_number,
        email: formData.guest.email,
        ip_address: ip,
      });

      const mapping = res?.data?.data;
      const guest = mapping?.guest;

      // Save only the REAL guest ID
      localStorage.setItem("extra_guest_id", guest.id);
      localStorage.setItem("email", guest.email);
      localStorage.setItem("phone_number", guest.phone_number);
      localStorage.setItem("ip_address", ip);
      localStorage.setItem("username", guest.full_name);
      localStorage.setItem("is_otp_verified", "false");
      localStorage.removeItem("ZEGO_TOKEN");
      localStorage.removeItem("ZEGO_USER_ID");
      localStorage.removeItem("ZEGO_USER_NAME");
      // Update UI guest data correctly
    setEventDetail(prev => ({
        ...prev,
        members: {
          ...prev?.members,
          guest_detail: {
            id: guest.id,
            full_name: guest.full_name,
            phone_number: guest.phone_number,
            email: guest.email,
            is_otp_verified: false
          },
        },
      }));

      setUserName(guest.full_name);
      setIsNewExtraGuest(false);
      setExtraGuestRegistered(true);


      message.success("Guest saved. Please verify OTP.");

    } catch (err) {
    message.error(err?.response?.data?.message || "Failed to save guest details");
    }
  };

  const handleOtpChange = (e) => {
    logger.info('handleOtpChange', e.target.value);
    // need to allow the characters and there will be max 4. it can be a combination of letters and numbers
    const next = e.target.value.toUpperCase().replace(/[^a-zA-Z0-9]/g, '').slice(0, 6);
    // always clear verification when user edits
    setOtpState(prev => ({ ...prev, value: next, error: '', verified: false }));
  };

  const sendOtpToGuest = async (isResend = false) => {
    if (!demoId) {
      messageApi.open({ type: 'error', content: 'Something went wrong with the demo, rejoin using link.' });
      return;
    }
    try {
      setOtpState(prev => ({ ...prev, sending: true }));
      const guestId = eventDetail?.members?.guest_detail?.id;
      await sendGuestOtp({ demo_id: demoId, guest_id: guestId, resend: !!isResend });
      messageApi.open({ type: 'success', content: isResend ? 'OTP resent.' : 'OTP sent.' });
      setOtpState(prev => ({ ...prev, value: '', verified: false, resendCountdown: 45, sentOnce: true, sending: false }));
    } catch (e) {
      messageApi.open({ type: 'error', content: e?.response?.data?.message || GENERIC_ERROR_MESSAGE });
      setOtpState(prev => ({ ...prev, sending: false }));
    }
  };

  const verifyOtpForGuest = async () => {
    if (!otpState.value || otpState.value.length < 4) {
      setOtpState(prev => ({ ...prev, error: 'Please enter a valid OTP (4-6 digits)' }));
      return;
    }
    try {
      setOtpState(prev => ({ ...prev, loading: true, error: '' }));
      const guestId = eventDetail?.members?.guest_detail?.id;
      await verifyGuestOtp({ demo_id: demoId, otp: otpState.value, guest_id: guestId });
      messageApi.open({ type: 'success', content: 'OTP verified. You can join now.' });
      setOtpState(prev => ({ ...prev, verified: true, loading: false }));
    } catch (e) {
      setOtpState(prev => ({ ...prev, verified: false, loading: false, error: e?.response?.data?.message || 'Invalid OTP, please try again.' }));
      messageApi.open({ type: 'error', content: e?.response?.data?.message || 'Invalid OTP' });
    }
  };

  /**
   * This function is used to check whether a host is an authorized host or not and manage the states accordingly.
   *
   * @return {void}
   */

  const authorizeHosts = async () => {
    let data;
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('fallback') === 'true') {
      data = {
        fallback: true,
        demoId,
      };
    } else {
      data = {
        demoId,
      };
    }
    try {
      await authorizeHost(data);
      setAuthorizationError(false);
    } catch (err) {
      logger.error('Invalid request detected in the catch block', err);
      setAuthorizationErrorMessage(
        err?.response?.data?.message || 'Invalid Request'
      );
      setAuthorizationError(true);
    } finally {
      setAuthorizationLoading(false);
    }
  };

  useEffect(() => {
    runGuestValidation().then(() => {
      getName();
      setGuestValidationCompleted(true);
    });

    // Test demos skip authorization
    if (isTestDemo) {
      setAuthorizationLoading(false);
      setAuthorizationError(false);
      return;
    }

    if (user && loggedIn) {
      authorizeHosts();
    } else {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('fallback') === 'true') {
        navigate(`/login/?returnUrl=${location.pathname}?fallback=true`);
      }
      setAuthorizationLoading(false);
    }
  }, []);
  useEffect(() => {
  const guestIsVerified = !!eventDetail?.members?.guest_detail?.is_otp_verified;
    if (guestIsVerified) {
    setOtpState(prev => ({ ...prev, verified: true, sentOnce: true }));
    } else {
      setOtpState((prev) => ({ ...prev, verified: false, sentOnce: false }));
    }
  }, [eventDetail]);

  /**
   * Handler for when Join Call button is clicked
   * For guests, shows Terms modal first; for hosts, proceeds directly
   */
  const handleJoinCallClick = () => {
    // Guest flow - show terms modal first
    if (!user || !loggedIn) {
      setShowTermsModal(true);
      return;
    }
    
    // Host flow - proceed directly to join
    handleJoinClick();
  };

  /**
   * Handler for Terms acceptance - proceeds with join
   */
  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    handleJoinClick();
  };

  /**
   * Handler for Terms rejection - close modal
   */
  const handleTermsReject = () => {
    setShowTermsModal(false);
    setTermsAccepted(false);
  };

  /**
   * This function is called when a host joins the meeting room from the waiting room and handles the states
   *
   * @param {void}
   * @return {void}
   */

  const handleJoinClick = async () => {
    if (validateForm()) {
      // Clean up preview before navigation
      // destroyPreview();

      // Wait a moment for cleanup to complete
      const waitingTime = 100;
      await new Promise(resolve => setTimeout(resolve, waitingTime));

      localStorage.setItem('username', userName);
      localStorage.setItem('demoId', eventDetail?.id);
      setJoinLoading(true);

      // 🔥 NEW: Collect device information
      const deviceInfo = collectDeviceInfo();
      console.log('Device Info collected:', deviceInfo);
      const mediaPermissions = await getMediaPermissions();
      console.log('Media Permissions:', mediaPermissions);

      // Test demo simplified flow - skip API calls, just generate token and navigate
      if (isTestDemo) {
        const testUserId = 'test-user-id';
        const testUserName = userName || 'Test User';
        const ExpirationTime = 3600;
        const token = ZegoUIKitPrebuilt.generateKitTokenForTest(
          parseInt(appID),
          serverSecret,
          demoId,
          testUserId,
          testUserName,
          ExpirationTime
        );

        localStorage.setItem('ZEGO_TOKEN', token);
        setDemoId(eventDetail?.id);

        const data = {
          sessionId: eventDetail?.id,
          token,
        };
        setVideoCredentials(data);

        const meetingData = {
          name: testUserName,
          id: testUserId,
          sessionDetail: eventDetail,
          videoState: localVideo,
          audioState: localAudio,
        };
        setDataForMeeting(meetingData);
        sessionStorage.setItem("cameFromWaitingRoom", "true");

        navigate(`/demo/room/${userName}`, {
          state: {
            ...meetingData,
            allowRoomStart: true,
          },
        });

        setJoinLoading(false);
        return;
      }

      if (user && loggedIn) {
        let hostId = eventDetail?.members?.host_detail?.id;
        if (!hostId) {
          hostId = user?.uid;
        }
        let hostName = 
          eventDetail?.members?.host_detail?.first_name &&
          eventDetail?.members?.host_detail?.last_name
            ? `${eventDetail.members.host_detail.first_name} ${eventDetail.members.host_detail.last_name}`
            : `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

        if (!hostName) {
          hostName = user?.firstName;
        }
        
        setDemoId(eventDetail?.id);

        if (eventDetail?.status !== 'STARTED') {
          let response;
          const queryParams = new URLSearchParams(window.location.search);

          if (queryParams.get('fallback') === 'true') {
            response = getVideoCredentials(eventDetail?.id, {
              meeting: true,
              fallback: true,
              device: "web",
              user: "host",
              device_info: deviceInfo,
              audio: mediaPermissions.audio,
              video: mediaPermissions.video,
            });
          } else {
            response = getVideoCredentials(eventDetail?.id, {
              meeting: true,
              device: "web",
              user: "host",
              device_info: deviceInfo,
              audio: mediaPermissions.audio,
              video: mediaPermissions.video,
            });
          }

          response
            .then((res) => {
              const backendMeetingDetails = res?.data?.data?.meeting_details || {};
              const backendHostToken = backendMeetingDetails?.host_token;

              if (!backendHostToken) {
                logger.error("Host token not received from backend");
              }

              localStorage.setItem('ZEGO_TOKEN', backendHostToken);

              const credentials = {
                sessionId: eventDetail?.id,
                token: backendHostToken,
              };
              setVideoCredentials(credentials);
              // Clean up video and audio streams before redirecting
              cleanupStreams();

              const data = {
                name: hostName,
                id: backendMeetingDetails?.host_zego_user_id || String(hostId),
                sessionDetail: {
                  ...eventDetail,
                  meeting_details: {
                    ...eventDetail?.meeting_details,
                    ...backendMeetingDetails,
                  },
                },
                videoState: localVideo,
                audioState: localAudio,
              };
              setDataForMeeting(data);
              sessionStorage.setItem('cameFromWaitingRoom', 'true');
              navigate(`/demo/room/${userName}`, {
                state: {
                  ...data,
                  allowRoomStart: true,
                },
              });
            })
            .catch((e) => {
              messageApi.open({
                type: 'error',
                content: GENERIC_ERROR_MESSAGE,
              });
              logger.error('Invalid request detected while joining', e);
              setAuthorizationErrorMessage(
                e?.response?.data?.message || 'Invalid Request'
              );
              setAuthorizationError(true);
            })
            .finally(() => {
              setJoinLoading(false);
            });
        } else {
          const backendMeetingDetails = eventDetail?.meeting_details || {};
          const backendHostToken = backendMeetingDetails?.host_token;
          if (!backendHostToken) {
            messageApi.open({
              type: 'error',
              content: 'Host token not found. Please refresh and try again.',
            });
            setJoinLoading(false);
            return;
          }

          localStorage.setItem('ZEGO_TOKEN', backendHostToken);

          const credentials = {
            sessionId: eventDetail?.id,
            token: backendHostToken,
          };
          setVideoCredentials(credentials);

          const res = setHostPresence(eventDetail?.id, {
            status: true,
            device: "web",
            user: "host",
            device_info: deviceInfo,
            audio: mediaPermissions.audio,
            video: mediaPermissions.video,
          });

          res
            .then(() => {
              // Clean up video and audio streams before redirecting
              cleanupStreams();

              const data1 = {
                name: backendMeetingDetails?.host_zego_user_name || hostName,
                id: backendMeetingDetails?.host_zego_user_id || String(hostId),
                sessionDetail: eventDetail,
                videoState: localVideo,
                audioState: localAudio,
              };
              setDataForMeeting(data1);
              sessionStorage.setItem('cameFromWaitingRoom', 'true');
              navigate(`/demo/room/${userName}`, {
                state: {
                  ...data1,
                  allowRoomStart: true,
                },
              });
            })
            .finally(() => {
              setJoinLoading(false);
            });
        }
      } else {
        // 🔥 GUEST JOIN LOGIC
        const guestID = eventDetail?.members?.guest_detail?.id;
        const guestName = eventDetail?.members?.guest_detail?.full_name;
        const backendMeetingDetails = eventDetail?.meeting_details || {};

        setDemoId(eventDetail?.id);

        const res = setGuestPresence(eventDetail?.id, {
          status: true,
          device: "web",
          user: "guest",
          user_id: guestID,
          device_info: deviceInfo,
          audio: mediaPermissions.audio,
          video: mediaPermissions.video,
        });

        res
          .then((response) => {
            const zegoData = response?.data?.zego_data || {};

            const backendGuestToken = zegoData?.zego_token;
            const guestZegoUserId = zegoData?.zego_user_id || String(guestID || "");
            const guestZegoUserName = zegoData?.zego_user_name || guestName || "Guest";

            if (!backendGuestToken) {
              messageApi.open({
                type: 'error',
                content: 'Guest token not found. Please refresh and try again.',
              });
              setJoinLoading(false);
              return;
            }
            // Clean up video and audio streams before redirecting
            cleanupStreams();

            const data = {
              sessionId: eventDetail?.meeting_details?.session_id,
              token: backendGuestToken,
            };
            localStorage.setItem('ZEGO_TOKEN', backendGuestToken);
            localStorage.setItem('ZEGO_USER_ID', guestZegoUserId);
            localStorage.setItem('ZEGO_USER_NAME', guestZegoUserName);
            setVideoCredentials(data);

            const data1 = {
              name: guestZegoUserName,
              id: guestZegoUserId,
              sessionDetail: {
                ...eventDetail,
                meeting_details: {
                  ...backendMeetingDetails,
                  guest_token: backendGuestToken,
                  guest_zego_user_id: guestZegoUserId,
                  guest_zego_user_name: guestZegoUserName,
                  guests: {
                    ...(backendMeetingDetails?.guests || {}),
                    [guestID]: {
                      token: backendGuestToken,
                      zego_user_id: guestZegoUserId,
                      zego_user_name: guestZegoUserName,
                    },
                  },
                },
              },
              videoState: localVideo,
              audioState: localAudio
            };
            setDataForMeeting(data1);
            sessionStorage.setItem('cameFromWaitingRoom', 'true');
            navigate(`/demo/room/${userName}`, {
              state: {
                ...data1,
                allowRoomStart: true,
              },
            });
          })
          .catch(err => {
            messageApi.open({
              type: 'error',
              content: err?.response?.data?.message || GENERIC_ERROR_MESSAGE,
            });
          })
          .finally(() => {
            setJoinLoading(false);
          });
      }
    }
  };

  const validateForm = () => {
    if (userName === '') {
      setIsUserNameInvalid(true);
      return false;
    }
    return true;
  };

  const onChangeParticipantName = (e) => {
    const userName = e.target.value;
    if (userName === '' || userName.trim() === '') {
      setUserName('');
      return;
    }
    setIsUserNameInvalid(false);
    setUserName(userName);
  };

  const onKeyDown = (e) => {
    if (e.keyCode === 13) {
      handleJoinCallClick();
    }
  };

  useEffectOnce(() => {
    setUser({
      ...user,
      videoEffects: {
        backgroundBlur: false,
        virtualBackground: false,
      },
      defaultSettings: {
        publishAudio: localAudio,
        publishVideo: localVideo,
        audioSource: undefined,
        videoSource: undefined,
        audioOutput: undefined,
      },
    });
  }, []);

  useEffect(() => {
    redirectHttps();
  }, [redirectHttps]);

  useEffect(() => {
    if (userName !== user?.userName) {
      setUser({ ...user, userName: userName });
    }
  }, [userName, user, setUser]);

  // Detect camera facing BEFORE creating preview so we can add mirror class


  useEffect(() => {
    if(otpState.resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setOtpState(prev => ({ ...prev, resendCountdown: prev.resendCountdown > 0 ? prev.resendCountdown - 1 : 0 }));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpState.resendCountdown]);

  if (authorizationLoading) {
    return (
      <>
        <PurebrandLoader />
      </>
    );
  }

  if (authorizationError) {
    logger.error('authorizationErrorMessage', authorizationErrorMessage);
    return <h2>{authorizationErrorMessage}</h2>;
  }
  if (!guestValidationCompleted) {
    return <PurebrandLoader />;   // Loading spinner, no video DOM
  }

  // Host-left / demo-over state for guests: when the demo is no longer active
  // (ended, canceled, or declined) there is no live call to join, so show a
  // clear explanation instead of the normal waiting room + Join Call button.
  // NOTE: this is status-based, so it cannot detect a host who walked away
  // while the demo is still STARTED -- that needs a real-time presence signal.
  const inactiveDemoStatuses = ['ENDED', 'CANCELED', DEMO_STATUS_OPTIONS.DECLINED];
  const isDemoCanceled =
    eventDetail?.status === 'CANCELED' ||
    eventDetail?.status === DEMO_STATUS_OPTIONS.DECLINED;
  const moderation = eventDetail?.moderation;
  if (moderation?.demo_terminated || (!loggedIn && moderation?.guest_removed)) {
    return (
      <>
        {contextHolder}
        <div className="container waiting-room-wrapper">
          <div
            className="demo-inactive-wrap d-flex flex-column align-items-center text-center"
            style={{ padding: '60px 20px', gap: '16px', maxWidth: '560px', margin: '0 auto' }}
          >
            <InfoCircleFill className="text-danger" style={{ fontSize: '48px' }} />
            <h2>{moderation?.demo_terminated ? 'Meeting ended' : 'Removed from meeting'}</h2>
            <p className="text-muted">
              {moderation?.message ||
                'You cannot join this meeting because of a content-policy violation.'}
            </p>
          </div>
        </div>
      </>
    );
  }
  if (!loggedIn && inactiveDemoStatuses.includes(eventDetail?.status)) {
    const hostFirstName = eventDetail?.members?.host_detail?.first_name;
    const storeName = eventDetail?.store?.name;
    const storeDomain = eventDetail?.store?.domain;
    const rescheduleUrl = eventDetail?.store_reschedule_url;
    return (
      <>
        {contextHolder}
        <div className="container waiting-room-wrapper">
          <div
            className="demo-inactive-wrap d-flex flex-column align-items-center text-center"
            style={{ padding: '60px 20px', gap: '16px', maxWidth: '560px', margin: '0 auto' }}
          >
            <InfoCircleFill className="text-info" style={{ fontSize: '48px' }} />
            <h2>{isDemoCanceled ? 'This demo was canceled' : 'This demo has ended'}</h2>
            <p className="text-muted">
              {isDemoCanceled
                ? `${hostFirstName || 'Your host'} canceled this session, so there's no live call to join right now.`
                : `${hostFirstName || 'Your host'} has left and this demo is no longer active. Thanks for stopping by!`}
            </p>
            {rescheduleUrl ? (
              <a href={rescheduleUrl} className="btn btn-primary">
                Reschedule a demo
              </a>
            ) : storeDomain ? (
              <a href={`https://${storeDomain}`} className="btn btn-primary">
                Back to {storeName || 'the store'}
              </a>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  // NEW: if guest is new, show only the form (no waiting room)
  if (!loggedIn && isNewExtraGuest && !extraGuestRegistered){
    return (
      <>
        {contextHolder}
        <div className="container waiting-room-wrapper container">

          <div className="extra-guest-full-page-wrapper">
            <ExtraGuestForm onSubmit={handleExtraGuestSubmit} host={eventDetail?.members?.host_detail} storeCategories={storeCategories} />
          </div>
        </div>
      </>
    );
  }

  // requestCameraMicPermissions();
  return (
    <>
      {contextHolder}
      <div className="container waiting-room-wrapper container">

        <div className="row waiting-contents">
          <div className="waiting-video-wrap d-flex flex-column col-md-8 col-sm-12">
            
            {shouldShowWaitingRoom && (
              <div className={isFrontCamera ? "mirror-video" : ""}>
                <WaitingRoomPreview
                  setCameraMicrophonePermission={setCameraMicrophonePermission}
                  setIsFrontCamera={setIsFrontCamera}
                  cleanupStreams={cleanupStreams}
                />
              </div>
            )}
            {/* <div className="mt-4 waiting-video-controller-wrap">
              <AudioSettings
                hasAudio={localAudio}
                onAudioChange={handleAudioChange}
              />
              <VideoSettings
                hasVideo={localVideo}
                onVideoChange={handleVideoChange}
              />
            </div> */}
            {!cameraMicrophonePermission && (
              <div className="text-container-wrap ">
                <div className="point-text-wrap">
                  Before you Join the Call, <br />
                  Make Sure You have turned on your Camera and Microphone
                  permission.
                </div>
              </div>
            )}
            {user && loggedIn && (
              <>
                <div className="text-start pt-5">
                  <InfoCircleFill
                    className="text-info"
                    style={{ fontSize: '30px', paddingRight: '10px' }}
                  />
                  To be eligible for your commission regardless of if the
                  customer joins, we ask you to please wait on the call for at
                  least 10 minutes to give the guest adequate time to join
                </div>
              </>
            )}
          </div>
          <form
            noValidate
            className="col-md-4 col-sm-12 details-content-wrapper"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="d-flex flex-column gap-2 details-wrap">
              <h3>What's your name?</h3>
              <input
                id="publisher-name"
                name="name"
                className="form-control mb-2"
                required
                placeholder="Your name"
                autoComplete="Name"
                value={userName}
                onChange={onChangeParticipantName}
                onKeyDown={onKeyDown}
                disabled={!isTestDemo && !hasUrlUserName}
              />
              {/* OTP input - show only for guests when OTP required and not yet verified */}
              {user && !loggedIn && isOtpRequired() && !otpState.verified && (
                <div className="form-group mb-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <label htmlFor="waiting-otp-input" className="mb-0">
                      {otpState.sentOnce ? (
                        <> Enter OTP sent to{" "} <strong>{eventDetail?.members?.guest_detail?.phone_number ? formatPhoneNumber(eventDetail.members.guest_detail.phone_number) : 'your phone'}</strong>{" "} to join the demo </>
                      ) : (
                        <> Click to send the OTP to{" "} <strong>{eventDetail?.members?.guest_detail?.phone_number ? formatPhoneNumber(eventDetail.members.guest_detail.phone_number) : 'your phone'}</strong> </>
                      )}
                    </label>
                    {!otpState.sentOnce && (
                      <div
                        className="btn btn-primary"
                        style={{
                          borderRadius: '10px',
                          opacity: otpState.sending ? 0.6 : 1,
                          cursor: otpState.sending ? 'not-allowed' : 'pointer',
                          pointerEvents: otpState.sending ? 'none' : 'auto'
                        }}
                        onClick={() => {
                          logger.info('Sending OTP');
                          if (!otpState.sending) {
                            sendOtpToGuest(otpState.sentOnce);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            logger.info('Sending OTP..');
                            if (!otpState.sending) {
                              sendOtpToGuest(otpState.sentOnce);
                            }
                          }
                        }}
                      >
                        Send OTP
                      </div>
                    )}
                    {otpState.sentOnce && (
                      <div
                        className="btn btn-primary"
                        style={{
                          borderRadius: '10px',
                          opacity: (otpState.verified || otpState.sending || otpState.resendCountdown > 0) ? 0.6 : 1,
                          cursor: (otpState.verified || otpState.sending || otpState.resendCountdown > 0) ? 'not-allowed' : 'pointer',
                          pointerEvents: (otpState.verified || otpState.sending || otpState.resendCountdown > 0) ? 'none' : 'auto'
                        }}
                        onClick={() => {
                          if (!otpState.verified && !otpState.sending && otpState.resendCountdown === 0) {
                            sendOtpToGuest(otpState.sentOnce);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!otpState.verified && !otpState.sending && otpState.resendCountdown === 0) {
                              sendOtpToGuest(otpState.sentOnce);
                            }
                          }
                        }}
                      >
                        {otpState.resendCountdown > 0
                          ? `Resend (${otpState.resendCountdown})`
                          : "Resend"}
                      </div>
                    )}
                  </div>
                  {otpState.sentOnce && (
                    <div className="d-flex align-items-center gap-2">
                      <input
                        id="waiting-otp-input"
                        className={`form-control ${otpState.error ? 'is-invalid' : ''}`}
                        value={otpState.value}
                        onChange={handleOtpChange}
                        placeholder="4 character OTP"
                        maxLength={4}
                      />
                      <Button
                        type="primary"
                        onClick={verifyOtpForGuest}
                        loading={otpState.loading}
                        disabled={otpState.loading || otpState.value.length < 4 || otpState.verified}
                      >
                        {otpState.loading ? 'Verifying...' : 'Verify'}
                      </Button>
                    </div>
                  )}
                  {otpState.error && <div className="text-danger small mt-1">{otpState.error}</div>}
                </div>
              )}
              <OverlayTrigger
                key={'top'}
                placement={'top'}
                overlay={
                  <Tooltip id={`tooltip-${'top'}`}>
                    The meeting will be recorded.
                  </Tooltip>
                }
              >
                <button
                    color="primary"
                    onClick={handleJoinCallClick}
                    disabled={
                      !cameraMicrophonePermission ||
                      joinLoading ||
                      authorizationLoading ||
                      (user && !loggedIn && isOtpRequired() && !otpState.verified)
                    }
                    className="btn btn-primary btn-block"
                >
                  Join Call
                </button>
              </OverlayTrigger>
              <div
                className="btn btn-primary"
                style={{ borderRadius: '10px' }}
                onClick={() => {
                  setSendMessageBox(true);
                }}
              >
                Want to send your {loggedIn ? 'Guest' : 'Host'} a Message
              </div>
              <CustomModal
                showStatus={sendMessageBox}
                clickStatus={handleSendMessageClose}
                buttonMessage="Close"
                closeStatus={handleSendMessageClose}
                title={`Send your ${loggedIn ? 'Guest' : 'Host'} a Message`}
                message={
                  <>
                    <div className="message-textarea">
                      <DemoMessageOut />
                    </div>
                  </>
                }
                secondButton={false}
                secondMessage="none"
              />
              {loggedIn ? (
                <div className="text-container-wrap two_wrap_demo">
                  <div className="points-content-wrap d-flex">
                    When you meet the guest, make sure you
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">1</div>
                    <div className="point-text-wrap">Introduce yourself</div>
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">2</div>
                    <div className="point-text-wrap">Show your product</div>
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">3</div>
                    <div className="point-text-wrap">
                      Tell them about installation or delivery
                    </div>
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">4</div>
                    <div className="point-text-wrap">Be respectful</div>
                  </div>
                </div>
              ) : (
                <div className="text-container-wrap two_wrap_demo">
                  <div className="points-content-wrap d-flex">
                    When you meet the host, make sure you
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">1</div>
                    <div className="point-text-wrap">Introduce yourself</div>
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">2</div>
                    <div className="point-text-wrap">
                      Ask about their experience
                    </div>
                  </div>
                  <div className="points-content-wrap d-flex">
                    <div className="numbering">3</div>
                    <div className="point-text-wrap">Be respectful</div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Terms and Conditions Modal for Guests */}
      <CustomModal
        showStatus={showTermsModal}
        clickStatus={handleTermsReject}
        buttonMessage="No, Exit Now"
        closeStatus={handleTermsReject}
        title="Terms and Conditions"
        message={
          <div className="terms-modal-content-wrapper">
            <GuestPopupTerms />
          </div>
        }
        secondButton={true}
        secondMessage="Yes, I Agree"
        secondButtonAction={handleTermsAccept}
      />
    </>
  );
}